import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { TDR } from 'tdr-common';
import config from '../common/config';
import { getTableDto, getTableWithPolicies } from './getTable';

type GetRestaurantDto = {
	slug: string;
	searchParams?: {
		groupSize?: number;
		date?: string;
		time?: string;
	};
}
type GetAvailabilityRestrictingPoliciesDto = {
	slug?: string;
	id?: string;
	searchParams?: {
		groupSize?: number;
		date?: string;
		time?: string;
	};
}

const CACHE_SETTINGS = {
	staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
	gcTime: 10 * 60 * 1000, // 10 minutes - data stays in cache for 10 minutes
	refetchOnMount: false, // Don't refetch when component remounts if data is still fresh
	refetchOnWindowFocus: false // Don't refetch when window regains focus
};

export function useEntityWithAvailabilityRestrictingPolicies(input: GetAvailabilityRestrictingPoliciesDto){
	const query = useQuery({
		enabled: !!input?.slug || !!input?.id,
		queryKey: ['getAvailabilityRestrictingPolicies', input],
		queryFn: () => getEntityWithAvailabilityRestrictingPolicies(input),
		...CACHE_SETTINGS
	});
	return query;
}

async function getEntityWithAvailabilityRestrictingPolicies(input: GetAvailabilityRestrictingPoliciesDto): Promise<TDR.EnrichedRestaurant | TDR.TableWithPolicies | null> {
	return input.id ? getTableWithPolicies(input as getTableDto) : getRestaurantBySlug(input as GetRestaurantDto);
}

export function useRestaurantBySlug(input: GetRestaurantDto) {

	const query = useQuery({
		enabled: !!input?.slug,
		queryKey: ['getRestaurantBySlug', input],
		queryFn: () => getRestaurantBySlug(input),
		...CACHE_SETTINGS
	});

	return query;
}

/**
 * Hook for fetching restaurant by slug with React Suspense
 * Use this in components wrapped with <Suspense> for automatic loading states
 * @throws {Promise} Suspends while loading
 */
export function useRestaurantBySlugSuspense(input: GetRestaurantDto) {
	const query = useSuspenseQuery({
		queryKey: ['getRestaurantBySlug', input],
		queryFn: () => getRestaurantBySlug(input),
		...CACHE_SETTINGS
	});

	return query;
}

export async function getRestaurantBySlug(
	input: GetRestaurantDto
): Promise<TDR.EnrichedRestaurant | null> {
	const response = await fetch(`${config.adminApiHost}/restaurants`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(input)
	});

	if (response.status === 404) {
		return null;
	}

	const responsePayload = await response.json() as TDR.EnrichedRestaurant | null;

	return responsePayload;
}